import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Input,
  PageHeader,
  Spinner,
  Stat,
  Table,
  type TableColumn,
} from "../ds";
import {
  api,
  errorText,
  type BlocklistDeviceState,
  type BlocklistEntry,
  type BlocklistOverview,
} from "../lib/api";
import { useFleetEvents } from "../lib/hooks/useFleetEvents";

// Die zentrale WLAN-MAC-Blockliste (docs/device-management.md → Zentrale
// MAC-Blockliste): ein Bestand für die ganze Flotte, jede Änderung pusht der
// Server sofort auf alle verbundenen Router. Ein Eintrag ist eine volle MAC
// oder eine OUI (drei Oktette) — letztere sperrt den ganzen Hersteller.
//
// Der Anwendungsstand je Router kommt aus derselben Antwort. Live gehalten
// wird er über den Fleet-Stream: der Server feuert nach jedem angewandten Push
// ein device-Event, die Seite lädt dann ihre Übersicht neu (eine DB-Abfrage,
// kein Router wird geweckt).

export function BlocklistPage() {
  const [data, setData] = useState<BlocklistOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [mac, setMac] = useState("");
  const [comment, setComment] = useState("");

  const laden = useCallback(() => {
    api
      .getBlocklist()
      .then(setData)
      .catch((e: unknown) => setError(errorText(e)));
  }, []);

  useEffect(laden, [laden]);

  // Nach einem Push-Durchlauf feuern viele device-Events kurz hintereinander —
  // gesammelt neu laden statt einmal je Router.
  const timer = useRef<number | null>(null);
  useFleetEvents({
    onDevice: () => {
      if (timer.current !== null) return;
      timer.current = window.setTimeout(() => {
        timer.current = null;
        laden();
      }, 500);
    },
  });
  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    },
    [],
  );

  const schreiben = useCallback(async (aktion: () => Promise<BlocklistOverview>) => {
    setBusy(true);
    setError(null);
    try {
      // Jede schreibende Antwort ist die vollständige Übersicht.
      setData(await aktion());
      return true;
    } catch (e: unknown) {
      setError(errorText(e));
      return false;
    } finally {
      setBusy(false);
    }
  }, []);

  const hinzufuegen = async () => {
    if (!mac.trim()) return;
    const ok = await schreiben(() => api.addBlocklistEntry(mac.trim(), comment.trim()));
    if (ok) {
      setMac("");
      setComment("");
    }
  };

  const eintragSpalten: TableColumn<BlocklistEntry>[] = [
    { key: "mac", header: "MAC / OUI", mono: true, cell: (e) => e.mac },
    {
      key: "art",
      header: "Wirkung",
      cell: (e) =>
        e.isOui ? (
          <Badge variant="warn">Hersteller (OUI)</Badge>
        ) : (
          <Badge variant="neutral">einzelnes Gerät</Badge>
        ),
    },
    {
      key: "comment",
      header: "Kommentar",
      wrap: true,
      cell: (e) => e.comment || <span className="text-content-3">—</span>,
    },
    {
      key: "created",
      header: "Aufgenommen",
      hideOnMobile: true,
      cell: (e) => new Date(e.createdAt).toLocaleDateString("de-DE"),
    },
    {
      key: "actions",
      header: "",
      cell: (e) => (
        <div className="flex justify-end">
          <Button
            size="sm"
            variant="ghost"
            iconName="trash"
            title="Eintrag entfernen"
            disabled={busy}
            onClick={() => void schreiben(() => api.deleteBlocklistEntry(e.id))}
          />
        </div>
      ),
    },
  ];

  const geraeteSpalten: TableColumn<BlocklistDeviceState>[] = [
    { key: "host", header: "Router", cell: (d) => d.hostname || d.id },
    {
      key: "online",
      header: "Verbindung",
      cell: (d) =>
        d.online ? (
          <Badge variant="success">online</Badge>
        ) : (
          <Badge variant="neutral">offline</Badge>
        ),
    },
    {
      key: "applied",
      header: "Blockliste",
      cell: (d) =>
        d.applied ? (
          <Badge variant="success">angewandt</Badge>
        ) : (
          // Offline-Router holen sich den Stand beim nächsten Verbinden selbst.
          <Badge variant="warn">{d.online ? "ausstehend" : "beim nächsten Verbinden"}</Badge>
        ),
    },
  ];

  const angewandt = data?.devices.filter((d) => d.applied).length ?? 0;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="MAC-Blockliste"
        subtitle="Zentral gesperrte WLAN-Clients — wirkt auf allen Routern der Flotte, nur im WLAN."
      />

      {error && <Alert variant="danger">{error}</Alert>}

      {data === null ? (
        <div className="flex justify-center p-10">
          <Spinner />
        </div>
      ) : (
        <>
          <Card>
            <form
              className="flex flex-wrap items-end gap-3"
              onSubmit={(e) => {
                e.preventDefault();
                void hinzufuegen();
              }}
            >
              <Input
                label="MAC oder OUI"
                placeholder="a8:80:55:52:9f:1d oder a8:80:55"
                helperText="Eine OUI (drei Oktette) sperrt alle Geräte des Herstellers."
                value={mac}
                onChange={(e) => setMac(e.target.value)}
                className="font-mono"
                fieldClassName="min-w-64"
              />
              <Input
                label="Kommentar"
                placeholder="z.B. Tuya-Geräte Hohenlohe"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                fieldClassName="min-w-64 grow"
              />
              <Button type="submit" iconName="plus" disabled={busy || !mac.trim()}>
                Sperren
              </Button>
            </form>
          </Card>

          <Table
            columns={eintragSpalten}
            rows={data.entries}
            rowKey={(e) => e.id}
            emptyMessage="Keine Einträge — die Router behalten ihre lokale WLAN-Konfiguration."
          />

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <Stat label="Einträge" value={data.entries.length} iconName="lock" />
            <Stat label="Router angewandt" value={angewandt} iconName="check" />
            <Stat
              label="Router ausstehend"
              value={data.devices.length - angewandt}
              iconName="timer"
            />
          </div>

          <Table
            columns={geraeteSpalten}
            rows={data.devices}
            rowKey={(d) => d.id}
            emptyMessage="Keine Router in der Flotte."
          />
        </>
      )}
    </div>
  );
}
